import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Languages, Search, Check, ChevronDown } from 'lucide-react';

const PREFERRED = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'mr', label: 'Marathi', native: 'मराठी' },
];

const ALL_LANGUAGES = [
  { code: 'as', label: 'Assamese', native: 'অসমীয়া' },
  { code: 'bn', label: 'Bengali', native: 'বাংলা' },
  { code: 'bho', label: 'Bhojpuri', native: 'भोजपुरी' },
  { code: 'doi', label: 'Dogri', native: 'डोगरी' },
  { code: 'gu', label: 'Gujarati', native: 'ગુજરાતી' },
  { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ks', label: 'Kashmiri', native: 'कॉशुर' },
  { code: 'gom', label: 'Konkani', native: 'कोंकणी' },
  { code: 'mai', label: 'Maithili', native: 'मैथिली' },
  { code: 'ml', label: 'Malayalam', native: 'മലയാളം' },
  { code: 'mni', label: 'Manipuri', native: 'মৈতৈলোন্' },
  { code: 'ne', label: 'Nepali', native: 'नेपाली' },
  { code: 'or', label: 'Odia', native: 'ଓଡ଼ିଆ' },
  { code: 'pa', label: 'Punjabi', native: 'ਪੰਜਾਬੀ' },
  { code: 'raj', label: 'Rajasthani', native: 'राजस्थानी' },
  { code: 'sa', label: 'Sanskrit', native: 'संस्कृतम्' },
  { code: 'sat', label: 'Santali', native: 'ᱥᱟᱱᱛᱟᱲᱤ' },
  { code: 'sd', label: 'Sindhi', native: 'سنڌي' },
  { code: 'ta', label: 'Tamil', native: 'தமிழ்' },
  { code: 'te', label: 'Telugu', native: 'తెలుగు' },
  { code: 'ur', label: 'Urdu', native: 'اردو' },
];

export const ALL_LANG_MAP = Object.fromEntries(
  [...PREFERRED, ...ALL_LANGUAGES].map(l => [l.code, l])
);

/**
 * @param {string} activeLang - current language code
 * @param {string} slug - blog slug (for URL-based redirect)
 * @param {string} basePath - base path like "/blog"
 * @param {string[]} availableLanguages - list of language codes with ready translations
 * @param {string} variant - "light" or "dark"
 */
export const LanguageDropdown = ({ activeLang, slug, basePath = '/blog', availableLanguages, variant = 'light' }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const activeLabel = ALL_LANG_MAP[activeLang] || PREFERRED[0];
  const availSet = availableLanguages ? new Set(availableLanguages) : null;

  const handleSelect = (code) => {
    setOpen(false);
    setSearch('');
    if (code === activeLang) return;
    if (slug) {
      navigate(code === 'en' ? `${basePath}/${slug}` : `${basePath}/${code}/${slug}`);
    } else {
      navigate(code === 'en' ? basePath : `${basePath}/${code}`);
    }
  };

  // Filter preferred and all languages by availability
  const availPreferred = PREFERRED.filter(l => !availSet || availSet.has(l.code));
  const availAll = ALL_LANGUAGES.filter(l => (!availSet || availSet.has(l.code)) &&
    (l.label.toLowerCase().includes(search.toLowerCase()) || l.native.toLowerCase().includes(search.toLowerCase()))
  );

  const isDark = variant === 'dark';

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="language-dropdown-trigger"
        onClick={() => setOpen(!open)}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
          isDark
            ? 'border-white/20 text-white hover:bg-white/10'
            : 'border-slate-200 text-slate-700 hover:bg-slate-50'
        }`}
      >
        <Languages className="w-4 h-4" />
        <span>{activeLabel.native}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div className="absolute z-50 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden" style={{ right: 0 }}>
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
              <input
                data-testid="language-search-input"
                type="text"
                placeholder="Search language..."
                className="w-full pl-8 pr-3 py-2 text-sm bg-slate-50 rounded-lg border-none outline-none focus:ring-1 focus:ring-blue-500"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {availPreferred.length > 0 && (
              <div className="px-2 pt-2 pb-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-2 mb-1">Preferred</p>
                {availPreferred.map(lang => (
                  <button
                    key={lang.code}
                    data-testid={`lang-${lang.code}`}
                    onClick={() => handleSelect(lang.code)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      activeLang === lang.code ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <span>{lang.native} <span className="text-slate-400 text-xs ml-1">{lang.label}</span></span>
                    {activeLang === lang.code && <Check className="w-4 h-4 text-blue-600" />}
                  </button>
                ))}
              </div>
            )}
            {availAll.length > 0 && (
              <>
                <div className="border-t border-slate-100 mx-2" />
                <div className="px-2 pt-2 pb-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-2 mb-1">All Languages</p>
                  {availAll.map(lang => (
                    <button
                      key={lang.code}
                      data-testid={`lang-${lang.code}`}
                      onClick={() => handleSelect(lang.code)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeLang === lang.code ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span>{lang.native} <span className="text-slate-400 text-xs ml-1">{lang.label}</span></span>
                      {activeLang === lang.code && <Check className="w-4 h-4 text-blue-600" />}
                    </button>
                  ))}
                </div>
              </>
            )}
            {availPreferred.length <= 1 && availAll.length === 0 && (
              <p className="text-sm text-slate-400 px-4 py-3 text-center">
                {search ? 'No languages found' : 'Translations in progress...'}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
